import {
  MAX_MERCH_TWEEN_MS,
  MERCH_STYLE_DEFAULTS,
  MIN_MERCH_TWEEN_MS,
  MerchChangeAnimation,
  merchChangeAnimationOr
} from 'lib/merchStyle'
import {
  CHAT_FONT_WEIGHT_MAX,
  CHAT_FONT_WEIGHT_MIN,
  chatFontWeightCapability,
  effectiveChatFontWeight
} from 'lib/chatFonts'

export type MerchDisplayStyle = 'BOARD' | 'TICKER'

export const merchDisplayStyleOf = (overlay: unknown): MerchDisplayStyle => {
  const raw = overlay !== null && typeof overlay === 'object'
    ? (overlay as Record<string, unknown>).displayStyle
    : undefined
  return raw === 'TICKER' ? 'TICKER' : 'BOARD'
}

export const MAX_TICKER_MESSAGES = 20
export const MAX_TICKER_LABEL_LEN = 60
export const MAX_TICKER_TEXT_LEN = 200

export type MerchTickerLabelColor = 'green' | 'red' | 'white' | 'hide'

export const MERCH_TICKER_LABEL_COLORS: readonly MerchTickerLabelColor[] =
  ['green', 'red', 'white', 'hide']

export interface MerchTickerMessageDraft {
  key: string
  label: string
  labelColor: MerchTickerLabelColor
  text: string
}

let localMessageSeq = 0
export const freshMessageKey = (): string => `local-${++localMessageSeq}`

export const emptyTickerMessage = (): MerchTickerMessageDraft => ({
  key: freshMessageKey(),
  label: '',
  labelColor: 'green',
  text: ''
})

export const resolveTickerMessages = (raw: unknown): MerchTickerMessageDraft[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((m): m is Record<string, unknown> => m !== null && typeof m === 'object')
    .slice(0, MAX_TICKER_MESSAGES)
    .map((m) => ({
      key: typeof m.id === 'string' && m.id !== '' ? m.id : freshMessageKey(),
      label: typeof m.label === 'string' ? m.label.slice(0, MAX_TICKER_LABEL_LEN) : '',
      labelColor: MERCH_TICKER_LABEL_COLORS.includes(m.labelColor as MerchTickerLabelColor)
        ? m.labelColor as MerchTickerLabelColor
        : 'green',
      text: typeof m.text === 'string' ? m.text.slice(0, MAX_TICKER_TEXT_LEN) : ''
    }))
}

export interface MerchTickerStyleDraft {
  fontKey: string
  bottomBgColor: string
  bottomOpacity: number
  bottomPadTop: number
  bottomPadBottom: number
  pageBackground: string
  topBgColor: string
  textColor: string
  borderColor: string
  borderWidth: number
  gapHeight: number
  gapColor: string
  topPadTop: number
  topPadBottom: number
  topOuterPad: number
  bottomOuterPad: number
  scrollSpeedPxS: number
  changeAnimation: MerchChangeAnimation
  topTextSizePx: number
  topLineHeightPx: number
  bottomTextSizePx: number
  bottomLineHeightPx: number
  tweenMs: number
  bottomScrollReverse: boolean
  textGlowPx: number
  textGlowColor: string
  topSeparatorGapPx: number | null
  bottomSeparatorGapPx: number | null
  textGlowArrows: boolean
  priceSlotMinCents: number | null
  priceSlotMaxCents: number | null
  pctSlotMaxPct: number | null
  labelFontWeight: number | null
  messageFontWeight: number | null
}

export const MERCH_TICKER_STYLE_DEFAULTS: MerchTickerStyleDraft = {
  fontKey: 'VCR_OSD_MONO',
  bottomBgColor: '#000000',
  bottomOpacity: 1,
  bottomPadTop: 5,
  bottomPadBottom: 5,
  pageBackground: 'transparent',
  topBgColor: '#000000',
  textColor: '#ffffff',
  borderColor: '#ffffff',
  borderWidth: 0,
  gapHeight: 25,
  gapColor: 'transparent',
  topPadTop: 0,
  topPadBottom: 0,
  topOuterPad: 0,
  bottomOuterPad: 0,
  scrollSpeedPxS: 160,
  changeAnimation: 'FLASH',
  topTextSizePx: 43,
  topLineHeightPx: 50,
  bottomTextSizePx: 20,
  bottomLineHeightPx: 20,
  tweenMs: MERCH_STYLE_DEFAULTS.tweenMs,
  bottomScrollReverse: false,
  textGlowPx: 0,
  textGlowColor: '#ffffff40',
  topSeparatorGapPx: null,
  bottomSeparatorGapPx: null,
  textGlowArrows: true,
  priceSlotMinCents: null,
  priceSlotMaxCents: null,
  pctSlotMaxPct: null,
  labelFontWeight: null,
  messageFontWeight: null
}

export const MAX_TICKER_PAD_PX = 200
export const MAX_TICKER_BORDER_PX = 20
export const MAX_TICKER_GAP_PX = 200
export const MAX_TICKER_TEXT_GLOW_PX = 40
export const MIN_TICKER_SCROLL_SPEED_PX_S = 40
export const MAX_TICKER_SCROLL_SPEED_PX_S = 600
export const MIN_TICKER_TEXT_SIZE_PX = 8
export const MAX_TICKER_TEXT_SIZE_PX = 120
export const MIN_TICKER_LINE_HEIGHT_PX = 8
export const MAX_TICKER_LINE_HEIGHT_PX = 200
export const MAX_TICKER_PRICE_SLOT_CENTS = 1_000_000_000
export const MAX_TICKER_PCT_SLOT_PCT = 1000
export const MIN_MERCH_TICKER_FONT_WEIGHT = CHAT_FONT_WEIGHT_MIN
export const MAX_MERCH_TICKER_FONT_WEIGHT = CHAT_FONT_WEIGHT_MAX
export const MERCH_TICKER_WEIGHT_DEFAULT = 400

export const snapMerchTickerWeight = (fontKey: string, weight: number | null): number | null => {
  if (weight == null) return null
  if (chatFontWeightCapability(fontKey).kind === 'fixed') return null
  return effectiveChatFontWeight(fontKey, weight, MERCH_TICKER_WEIGHT_DEFAULT)
}

const HEX6_RE = /^#[0-9a-fA-F]{6}$/
const HEX_RE = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

const hex6Or = (v: unknown, fallback: string): string =>
  typeof v === 'string' && HEX6_RE.test(v) ? v : fallback

const hexOr = (v: unknown, fallback: string): string =>
  typeof v === 'string' && HEX_RE.test(v) ? v : fallback

const intOr = (v: unknown, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max ? v : fallback

const intRangeOr = <T extends number | null>(v: unknown, min: number, max: number, fallback: T): number | T =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max ? v : fallback

const pct1dpOr = <T extends number | null>(v: unknown, fallback: T): number | T =>
  typeof v === 'number' && Number.isFinite(v) && Number(v.toFixed(1)) === v && v >= 0 && v <= MAX_TICKER_PCT_SLOT_PCT
    ? v
    : fallback

const padOr = <T extends number | null>(v: unknown, fallback: T): number | T =>
  typeof v === 'number' && Number.isFinite(v) && Number(v.toFixed(2)) === v && v >= 0 && v <= MAX_TICKER_PAD_PX
    ? v
    : fallback

export const resolveTickerStyleDraft = (raw: unknown): MerchTickerStyleDraft => {
  const obj: Record<string, unknown> =
    raw !== null && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  return {
    fontKey: typeof obj.fontKey === 'string' && obj.fontKey !== ''
      ? obj.fontKey
      : MERCH_TICKER_STYLE_DEFAULTS.fontKey,
    bottomBgColor: hexOr(obj.bottomBgColor, MERCH_TICKER_STYLE_DEFAULTS.bottomBgColor),
    bottomOpacity: typeof obj.bottomOpacity === 'number' && obj.bottomOpacity >= 0 && obj.bottomOpacity <= 1
      ? obj.bottomOpacity
      : MERCH_TICKER_STYLE_DEFAULTS.bottomOpacity,
    bottomPadTop: padOr(obj.bottomPadTop, MERCH_TICKER_STYLE_DEFAULTS.bottomPadTop),
    bottomPadBottom: padOr(obj.bottomPadBottom, MERCH_TICKER_STYLE_DEFAULTS.bottomPadBottom),
    pageBackground: obj.pageBackground === 'transparent' || hex6Or(obj.pageBackground, '') !== ''
      ? obj.pageBackground as string
      : MERCH_TICKER_STYLE_DEFAULTS.pageBackground,
    topBgColor: hexOr(obj.topBgColor, MERCH_TICKER_STYLE_DEFAULTS.topBgColor),
    textColor: hexOr(obj.textColor, MERCH_TICKER_STYLE_DEFAULTS.textColor),
    borderColor: hexOr(obj.borderColor, MERCH_TICKER_STYLE_DEFAULTS.borderColor),
    borderWidth: intOr(obj.borderWidth, MAX_TICKER_BORDER_PX, MERCH_TICKER_STYLE_DEFAULTS.borderWidth),
    gapHeight: intOr(obj.gapHeight, MAX_TICKER_GAP_PX, MERCH_TICKER_STYLE_DEFAULTS.gapHeight),
    gapColor: obj.gapColor === 'transparent' || hexOr(obj.gapColor, '') !== ''
      ? obj.gapColor as string
      : MERCH_TICKER_STYLE_DEFAULTS.gapColor,
    topPadTop: padOr(obj.topPadTop, MERCH_TICKER_STYLE_DEFAULTS.topPadTop),
    topPadBottom: padOr(obj.topPadBottom, MERCH_TICKER_STYLE_DEFAULTS.topPadBottom),
    topOuterPad: padOr(obj.topOuterPad, MERCH_TICKER_STYLE_DEFAULTS.topOuterPad),
    bottomOuterPad: padOr(obj.bottomOuterPad, MERCH_TICKER_STYLE_DEFAULTS.bottomOuterPad),
    scrollSpeedPxS: intRangeOr(
      obj.scrollSpeedPxS,
      MIN_TICKER_SCROLL_SPEED_PX_S,
      MAX_TICKER_SCROLL_SPEED_PX_S,
      MERCH_TICKER_STYLE_DEFAULTS.scrollSpeedPxS
    ),
    changeAnimation: merchChangeAnimationOr(obj.changeAnimation, MERCH_TICKER_STYLE_DEFAULTS.changeAnimation),
    topTextSizePx: intRangeOr(
      obj.topTextSizePx,
      MIN_TICKER_TEXT_SIZE_PX,
      MAX_TICKER_TEXT_SIZE_PX,
      MERCH_TICKER_STYLE_DEFAULTS.topTextSizePx
    ),
    topLineHeightPx: intRangeOr(
      obj.topLineHeightPx,
      MIN_TICKER_LINE_HEIGHT_PX,
      MAX_TICKER_LINE_HEIGHT_PX,
      MERCH_TICKER_STYLE_DEFAULTS.topLineHeightPx
    ),
    bottomTextSizePx: intRangeOr(
      obj.bottomTextSizePx,
      MIN_TICKER_TEXT_SIZE_PX,
      MAX_TICKER_TEXT_SIZE_PX,
      MERCH_TICKER_STYLE_DEFAULTS.bottomTextSizePx
    ),
    bottomLineHeightPx: intRangeOr(
      obj.bottomLineHeightPx,
      MIN_TICKER_LINE_HEIGHT_PX,
      MAX_TICKER_LINE_HEIGHT_PX,
      MERCH_TICKER_STYLE_DEFAULTS.bottomLineHeightPx
    ),
    tweenMs: intRangeOr(
      obj.tweenMs,
      MIN_MERCH_TWEEN_MS,
      MAX_MERCH_TWEEN_MS,
      MERCH_TICKER_STYLE_DEFAULTS.tweenMs
    ),
    bottomScrollReverse: typeof obj.bottomScrollReverse === 'boolean'
      ? obj.bottomScrollReverse
      : MERCH_TICKER_STYLE_DEFAULTS.bottomScrollReverse,
    textGlowPx: intOr(obj.textGlowPx, MAX_TICKER_TEXT_GLOW_PX, MERCH_TICKER_STYLE_DEFAULTS.textGlowPx),
    textGlowColor: hexOr(obj.textGlowColor, MERCH_TICKER_STYLE_DEFAULTS.textGlowColor),
    topSeparatorGapPx: padOr(obj.topSeparatorGapPx, MERCH_TICKER_STYLE_DEFAULTS.topSeparatorGapPx),
    bottomSeparatorGapPx: padOr(obj.bottomSeparatorGapPx, MERCH_TICKER_STYLE_DEFAULTS.bottomSeparatorGapPx),
    textGlowArrows: typeof obj.textGlowArrows === 'boolean'
      ? obj.textGlowArrows
      : MERCH_TICKER_STYLE_DEFAULTS.textGlowArrows,
    priceSlotMinCents: intRangeOr(obj.priceSlotMinCents, 1, MAX_TICKER_PRICE_SLOT_CENTS, MERCH_TICKER_STYLE_DEFAULTS.priceSlotMinCents),
    priceSlotMaxCents: intRangeOr(obj.priceSlotMaxCents, 1, MAX_TICKER_PRICE_SLOT_CENTS, MERCH_TICKER_STYLE_DEFAULTS.priceSlotMaxCents),
    pctSlotMaxPct: pct1dpOr(obj.pctSlotMaxPct, MERCH_TICKER_STYLE_DEFAULTS.pctSlotMaxPct),
    labelFontWeight: intRangeOr(obj.labelFontWeight, MIN_MERCH_TICKER_FONT_WEIGHT, MAX_MERCH_TICKER_FONT_WEIGHT, MERCH_TICKER_STYLE_DEFAULTS.labelFontWeight),
    messageFontWeight: intRangeOr(obj.messageFontWeight, MIN_MERCH_TICKER_FONT_WEIGHT, MAX_MERCH_TICKER_FONT_WEIGHT, MERCH_TICKER_STYLE_DEFAULTS.messageFontWeight)
  }
}
